import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { api, formatApiError, formatCDF } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import MobileHeader from "../../components/MobileHeader";
import BottomNav from "../../components/BottomNav";
import AiInsightCard from "../../components/AiInsightCard";
import InstallAppPrompt from "../../components/InstallAppPrompt";
import { Card, Badge, Button } from "../../components/ui-kit";
import {
  MapPin,
  TicketX,
  Gift,
  Star,
  ArrowRight,
  Lightning,
  Motorcycle,
  Car,
  Warning,
  X,
  SquaresFour,
  Storefront,
  ShoppingCart,
  Van,
} from "@phosphor-icons/react";

const MARKETPLACE_VIDEO_ADS = [
  {
    title: "Repas livres",
    text: "Restaurants proches, panier unique et suivi du livreur.",
    image: "/marketplace/ad-restaurants.svg",
    to: "/app/marketplace/restaurants",
    tone: "primary",
  },
  {
    title: "Courses maison",
    text: "Supermarches, produits frais et livraison rapide.",
    image: "/marketplace/ad-supermarkets.svg",
    to: "/app/marketplace/supermarkets",
    tone: "secondary",
  },
  {
    title: "Tout arrive chez vous",
    text: "Taxi, colis, repas et courses dans la meme app.",
    image: "/marketplace/ad-delivery.svg",
    to: "/app/services",
    tone: "primary",
  },
];

const MARKETPLACE_GUIDES = [
  { title: "1. Choisissez", text: "Ouvrez Restaurants ou Supermarches, puis choisissez un seul partenaire." },
  { title: "2. Ajoutez", text: "Ajoutez les produits au panier. On ne melange pas plusieurs boutiques." },
  { title: "3. Confirmez", text: "Validez l'adresse, le telephone et le paiement. Nexora lance la livraison." },
];

export default function CustomerHome() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cities, setCities] = useState([]);
  const [cityName, setCityName] = useState("");
  const [promos, setPromos] = useState([]);
  const [loyalty, setLoyalty] = useState(null);
  const [activeRide, setActiveRide] = useState(null);
  const [recentRides, setRecentRides] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [geoMismatch, setGeoMismatch] = useState(null);
  const [dismissedGeo, setDismissedGeo] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [c, l, r] = await Promise.all([
          api.get("/cities"),
          api.get("/loyalty/me"),
          api.get("/rides"),
        ]);
        const cityList = Array.isArray(c.data) ? c.data : [];
        const rideList = Array.isArray(r.data) ? r.data : [];
        setCities(cityList);
        setLoyalty(l.data);
        setRecentRides(rideList.slice(0, 3));
        const mine = cityList.find((x) => x.id === user?.city_id);
        setCityName(mine ? mine.name : "");
        // active ride
        const act = rideList.find((rd) =>
          ["SEARCHING_DRIVER", "DRIVER_EN_ROUTE", "DRIVER_ARRIVED", "IN_PROGRESS"].includes(rd.status)
        );
        setActiveRide(act || null);
      } catch (e) {
        // silent
      }
    };
    load();
    // Try to fetch promos (need admin route, fallback to silent)
    api.post("/promos/validate", { code: "NEXORA10", amount: 3000 })
      .then(({ data }) => setPromos([data]))
      .catch(() => {});
    api.get("/marketplace/featured-products", { params: { limit: 6 } })
      .then(({ data }) => setFeaturedProducts(Array.isArray(data) ? data : []))
      .catch(() => setFeaturedProducts([]));

    // Geolocation detection — compare detected city with account city
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          try {
            const { data } = await api.get(
              `/cities/detect?lat=${pos.coords.latitude}&lng=${pos.coords.longitude}`
            );
            if (
              data.city &&
              user?.city_id &&
              data.city.id !== user.city_id &&
              data.within_service_area
            ) {
              setGeoMismatch(data.city);
            }
          } catch {}
        },
        () => {},
        { enableHighAccuracy: true, timeout: 18000, maximumAge: 0 }
      );
    }
  }, [user]);

  const tier = loyalty?.status;
  const tierColors = {
    bronze: "from-amber-700 to-amber-500",
    silver: "from-slate-400 to-slate-200",
    gold: "from-yellow-500 to-yellow-300",
    platinum: "from-cyan-400 to-purple-400",
  };

  return (
    <div className="phone-frame phone-scroll-safe bg-premium-grad">
      <MobileHeader
        action={
          <button className="max-w-[7.25rem] truncate rounded-full border border-white/10 bg-white/5 px-2.5 py-2 text-[9px] font-bold uppercase tracking-widest min-[380px]:max-w-[8.5rem] min-[380px]:text-[10px]" data-testid="city-pill">
            <MapPin size={12} weight="fill" className="mr-1 inline" /> {cityName || "—"}
          </button>
        }
      />

      <div className="space-y-5 px-4 pt-2">
        {!user?.city_id && (
          <Card className="border-primary/30 bg-primary/10 p-4" data-testid="home-city-required">
            <div className="flex items-start gap-3">
              <MapPin size={20} weight="fill" className="mt-0.5 shrink-0 text-primary" />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-bold">Choisissez votre ville</div>
                <div className="mt-1 text-xs text-muted-foreground">
                  Nexora doit verifier votre ville avec le GPS avant de commander une course.
                </div>
                <Button variant="primary" className="mt-3 py-2 text-xs" onClick={() => navigate("/app/profile")}>
                  Definir ma ville
                </Button>
              </div>
            </div>
          </Card>
        )}
        {/* Geo mismatch warning */}
        <AnimatePresence>
          {geoMismatch && !dismissedGeo && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              <div
                className="relative flex items-start gap-3 rounded-2xl border border-primary/40 bg-primary/10 p-4"
                data-testid="geo-mismatch-banner"
              >
                <Warning weight="fill" className="mt-0.5 shrink-0 text-primary" size={20} />
                <div className="flex-1 text-sm">
                  <div className="font-bold text-foreground">
                    Vous êtes à {geoMismatch.name}
                  </div>
                  <div className="mt-0.5 text-[11px] text-muted-foreground">
                    Votre compte est enregistré à {cityName}. Les courses sont limitées à votre ville d'origine.
                  </div>
                </div>
                <button
                  onClick={() => setDismissedGeo(true)}
                  className="rounded-full bg-white/10 p-1 press-down"
                  data-testid="dismiss-geo"
                >
                  <X size={14} weight="bold" />
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Greeting */}
        <div>
          <div className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">
            Bonjour
          </div>
          <h1 className="font-heading text-3xl font-bold">
            {user?.name?.split(" ")[0]} 👋
          </h1>
        </div>

        <InstallAppPrompt compact />

        <AiInsightCard title="Assistant IA client" limit={3} />

        {/* Active ride */}
        {activeRide && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            <Card className="p-4 glow-blue">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-widest text-primary">
                    Course en cours
                  </div>
                  <div className="font-heading text-lg font-bold">
                    #{activeRide.id.slice(0, 6).toUpperCase()}
                  </div>
                </div>
                <Link to={`/app/ride/${activeRide.id}`} data-testid="resume-ride">
                  <Button variant="primary" className="px-3 py-2 text-xs">Suivre</Button>
                </Link>
              </div>
            </Card>
          </motion.div>
        )}

        {/* Quick order */}
        <Link to="/app/ride" data-testid="quick-order-cta">
          <Card className="group overflow-hidden p-5 transition-colors hover:bg-white/10">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[11px] font-bold uppercase tracking-widest text-primary">Réserver</div>
                <div className="mt-1 font-heading text-2xl font-bold leading-tight">Où allez-vous ?</div>
                <div className="mt-1 text-xs text-muted-foreground">Tapez sur la carte · prix instantané</div>
              </div>
              <div className="rounded-2xl bg-primary/15 p-3 text-primary group-hover:bg-primary group-hover:text-primary-foreground">
                <ArrowRight size={24} weight="bold" />
              </div>
            </div>
            <div className="mt-4 flex gap-2">
              <Badge tone="primary"><Motorcycle size={10} weight="bold" /> Moto</Badge>
              <Badge tone="primary"><Car size={10} weight="bold" /> Economy</Badge>
              <Badge tone="default">Comfort</Badge>
              <Badge tone="default">Premium</Badge>
            </div>
          </Card>
        </Link>

        <Link to="/app/services" data-testid="services-cta">
          <Card className="group overflow-hidden p-4 transition-colors hover:bg-white/10">
            <div className="flex items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-3">
                <div className="rounded-2xl bg-secondary/15 p-3 text-secondary">
                  <SquaresFour size={24} weight="duotone" />
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-secondary">Services</div>
                  <div className="truncate font-heading text-lg font-black">Navette, tickets, evenements</div>
                  <div className="text-xs text-muted-foreground">Aeroport, ecole, stade, conference</div>
                </div>
              </div>
              <Van size={24} weight="duotone" className="shrink-0 text-primary" />
            </div>
          </Card>
        </Link>

        <div className="grid grid-cols-2 gap-3" data-testid="marketplace-shortcuts">
          <Link to="/app/marketplace/restaurants" data-testid="home-restaurants">
            <Card className="h-full p-4 press-down transition-colors hover:bg-white/10">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/15 text-primary">
                <Storefront size={24} weight="duotone" />
              </div>
              <div className="mt-3 text-[10px] font-bold uppercase tracking-widest text-primary">Food</div>
              <div className="mt-1 font-heading text-lg font-black leading-tight">Restaurants</div>
              <div className="mt-1 text-xs text-muted-foreground">Menus, panier et livraison.</div>
            </Card>
          </Link>
          <Link to="/app/marketplace/supermarkets" data-testid="home-supermarkets">
            <Card className="h-full p-4 press-down transition-colors hover:bg-white/10">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-secondary/15 text-secondary">
                <ShoppingCart size={24} weight="duotone" />
              </div>
              <div className="mt-3 text-[10px] font-bold uppercase tracking-widest text-secondary">Market</div>
              <div className="mt-1 font-heading text-lg font-black leading-tight">Supermarches</div>
              <div className="mt-1 text-xs text-muted-foreground">Courses et livraison.</div>
            </Card>
          </Link>
        </div>

        <div data-testid="home-marketplace-video-ads">
          <div className="mb-2 flex items-center justify-between">
            <h2 className="font-heading text-lg font-bold">Pubs partenaires</h2>
            <Badge tone="primary">Video</Badge>
          </div>
          <div className="flex snap-x gap-3 overflow-x-auto pb-1">
            {MARKETPLACE_VIDEO_ADS.map((ad) => (
              <Link
                key={ad.title}
                to={ad.to}
                className="group relative min-w-[250px] snap-start overflow-hidden rounded-3xl border border-white/10 bg-white/5 press-down"
              >
                <div className="h-36 overflow-hidden bg-black/20">
                  <img
                    src={ad.image}
                    alt=""
                    className="h-full w-full object-cover transition duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <Badge tone={ad.tone}>{ad.tone === "secondary" ? "Supermarche" : "Nexora"}</Badge>
                  <div className="mt-2 font-heading text-xl font-black">{ad.title}</div>
                  <div className="mt-1 text-xs text-white/75">{ad.text}</div>
                </div>
                <div className="absolute right-3 top-3 flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg shadow-primary/30">
                  <span className="ml-0.5 h-0 w-0 border-y-[7px] border-l-[11px] border-y-transparent border-l-current" />
                </div>
              </Link>
            ))}
          </div>
        </div>

        {featuredProducts.length > 0 && (
          <div data-testid="home-product-ads">
            <div className="mb-2 flex items-center justify-between">
              <h2 className="font-heading text-lg font-bold">Produits proches</h2>
              <Badge tone="primary">Pubs</Badge>
            </div>
            <div className="product-ad-marquee overflow-hidden pb-2">
              <div className="product-ad-marquee-track flex w-max gap-3">
              {[...featuredProducts, ...featuredProducts].map((product, index) => {
                const marketplaceType = product.merchant_type === "supermarket"
                  ? "supermarkets"
                  : product.merchant_type === "pharmacy"
                    ? "pharmacies"
                    : "restaurants";
                return (
                  <Link
                    key={`home-product-${product.id}-${index}`}
                    to={`/app/marketplace/${marketplaceType}`}
                    className="w-[166px] shrink-0 overflow-hidden rounded-3xl border border-white/10 bg-white/5 press-down min-[390px]:w-[184px]"
                  >
                    <div className="h-28 overflow-hidden bg-black/20">
                      {product.image_url ? (
                        <img src={product.image_url} alt="" className="h-full w-full object-cover" />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center text-primary">
                          <ShoppingCart size={30} weight="duotone" />
                        </div>
                      )}
                    </div>
                    <div className="p-3">
                      <div className="line-clamp-2 min-h-[2.15rem] font-heading text-sm font-black leading-tight">{product.name}</div>
                      <div className="mt-1 truncate text-[10px] text-muted-foreground">{product.merchant_name || "Partenaire Nexora"}</div>
                      <div className="mt-2 whitespace-nowrap font-mono text-[12px] font-black text-primary">{formatCDF(product.price)}</div>
                    </div>
                  </Link>
                );
              })}
              </div>
            </div>
          </div>
        )}

        <Card className="p-4" data-testid="marketplace-guides">
          <div className="mb-3 flex items-center justify-between">
            <div>
              <div className="text-[10px] font-black uppercase tracking-widest text-primary">Guide rapide</div>
              <h2 className="font-heading text-lg font-black">Commander sans confusion</h2>
            </div>
            <Badge tone="success">Simple</Badge>
          </div>
          <div className="grid gap-2">
            {MARKETPLACE_GUIDES.map((guide) => (
              <div key={guide.title} className="rounded-2xl border border-white/10 bg-white/5 p-3">
                <div className="font-heading text-sm font-black">{guide.title}</div>
                <div className="mt-1 text-xs leading-5 text-muted-foreground">{guide.text}</div>
              </div>
            ))}
          </div>
        </Card>

        {/* Loyalty card */}
        {tier && (
          <Card className="overflow-hidden p-0">
            <div className={`bg-gradient-to-r ${tierColors[tier.tier] || tierColors.bronze} px-5 py-4`}>
              <div className="flex items-center justify-between text-background">
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-widest opacity-80">NEXORA · Fidélité</div>
                  <div className="font-heading text-2xl font-bold">{tier.tier_label}</div>
                </div>
                <Star size={36} weight="fill" className="opacity-90" />
              </div>
            </div>
            <div className="p-4">
              <div className="flex items-end justify-between">
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Points</div>
                  <div className="font-heading text-3xl font-bold" data-testid="loyalty-points">
                    {tier.points}
                  </div>
                </div>
                {tier.next_tier && (
                  <div className="text-right text-xs text-muted-foreground">
                    <div className="font-bold text-foreground">
                      +{tier.points_to_next} pts
                    </div>
                    <div>vers {tier.next_tier_label}</div>
                  </div>
                )}
              </div>
              {tier.next_tier && (
                <div className="mt-3 h-2 overflow-hidden rounded-full bg-white/10">
                  <div
                    className="h-full bg-gradient-to-r from-primary to-secondary"
                    style={{
                      width: `${Math.min(100, (tier.points / (tier.points + tier.points_to_next)) * 100 || 0)}%`,
                    }}
                  />
                </div>
              )}
            </div>
          </Card>
        )}

        {/* Promo */}
        {promos.length > 0 && (
          <Card className="flex items-center justify-between gap-3 border-secondary/30 p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-xl bg-secondary/15 p-2 text-secondary">
                <Gift size={22} weight="duotone" />
              </div>
              <div>
                <div className="font-heading font-bold">{promos[0].code}</div>
                <div className="text-xs text-muted-foreground">-{formatCDF(promos[0].discount)} de réduction</div>
              </div>
            </div>
            <Badge tone="success">Actif</Badge>
          </Card>
        )}

        {/* Recent rides */}
        <div>
          <div className="mb-2 flex items-center justify-between">
            <h2 className="font-heading text-lg font-bold">Dernières courses</h2>
            <Link to="/app/profile" className="text-xs font-bold text-primary" data-testid="see-all-rides">Tout voir</Link>
          </div>
          {recentRides.length === 0 ? (
            <Card className="p-6 text-center">
              <div className="text-xs text-muted-foreground">Aucune course — commandez la première !</div>
            </Card>
          ) : (
            <div className="space-y-2">
              {recentRides.map((r) => (
                <Link key={r.id} to={`/app/ride/${r.id}`} data-testid={`recent-${r.id}`}>
                  <Card className="flex items-center justify-between p-3">
                    <div className="flex items-center gap-3">
                      <div className="rounded-xl bg-primary/10 p-2 text-primary">
                        {r.vehicle_type === "moto" ? <Motorcycle size={18} weight="fill" /> : <Car size={18} weight="fill" />}
                      </div>
                      <div>
                        <div className="text-xs font-bold">
                          {r.vehicle_type} · #{r.id.slice(0, 6).toUpperCase()}
                        </div>
                        <div className="text-[10px] text-muted-foreground">
                          {new Date(r.created_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono text-sm font-bold">{formatCDF(r.price)}</div>
                      <div className="text-[10px] text-muted-foreground">{r.status}</div>
                    </div>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      <BottomNav role="customer" />
    </div>
  );
}
